import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Link2, Lock } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import SEO from "@/components/SEO";
import NotFound from "./NotFound";
import { useI18n } from "@/i18n";
import { identifyVisitor, clientStatusForWire } from "@/lib/fingerprint";
import { getBlogViewFingerprint } from "@/lib/blogViewFingerprint";
import { awaitPendingVisits, getNavigationContext } from "@/lib/trace";

type State = "loading" | "password" | "inactive" | "notfound";

const Redirect = () => {
  const { code } = useParams<{ code: string }>();
  const location = useLocation();
  const navKey = location.key ?? `${location.pathname}${location.search}`;
  const { t } = useI18n();
  const [state, setState] = useState<State>("loading");
  const [title, setTitle] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [wrongPassword, setWrongPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const resolve = useCallback(
    async (pwd?: string) => {
      if (!code) return;
      // Exactement le même contexte de navigation que VisitorTracker,
      // capturé avant toute attente.
      const ctx = getNavigationContext(navKey);
      const fp = await identifyVisitor(undefined, {
        traceId: ctx.traceId,
        sessionId: ctx.sessionId,
      });
      const { data, error: fnError } = await supabase.functions.invoke("resolve-link", {
        body: {
          code,
          password: pwd ?? null,
          referrer: document.referrer || null,
          fingerprint: getBlogViewFingerprint() || null,
          fp_request_id: fp.requestId,
          fp_status: clientStatusForWire(fp),
          trace_id: ctx.traceId,
          session_id: ctx.sessionId,
        },
      });





      let payload = data as
        | {
            found?: boolean;
            active?: boolean;
            requires_password?: boolean;
            invalid_password?: boolean;
            target_url?: string;
            title?: string | null;
          }
        | null;

      if (fnError) {
        try {
          const ctx = (fnError as { context?: Response }).context;
          if (ctx) payload = JSON.parse(await ctx.text());
        } catch {
          /* ignore */
        }
      }

      if (!payload?.found) {
        setState("notfound");
        return;
      }
      if (payload.active === false) {
        setState("inactive");
        return;
      }
      if (payload.requires_password) {
        setTitle(payload.title ?? null);
        if (payload.invalid_password) setWrongPassword(true);
        setState("password");
        return;
      }
      if (payload.target_url) {
        // Laisse l'enregistrement de la visite associée se terminer, avec une
        // borne stricte (voir PENDING_VISIT_BUDGET_MS = 1200 ms) : un échec ou
        // un dépassement n'empêche jamais la redirection.
        await awaitPendingVisits();
        window.location.replace(payload.target_url);
        return;
      }
      setState("notfound");
    },
    [code, navKey],
  );

  useEffect(() => {
    resolve();
  }, [resolve]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setWrongPassword(false);
    try {
      await resolve(password);
    } finally {
      setSubmitting(false);
    }
  };

  if (state === "notfound") return <NotFound />;

  return (
    <div className="min-h-screen bg-background px-4 py-20">
      <SEO title={t("redirect.seo.title")} description={t("redirect.seo.description")} noindex />
      <div className="mx-auto w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2 font-display text-lg font-bold">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-glow">
            <Link2 className="h-4 w-4" />
          </span>
          {t("brand.name")}
        </Link>

        {state === "loading" && (
          <div className="flex flex-col items-center gap-4 text-muted-foreground">
            <span className="flex h-14 w-14 animate-pulse items-center justify-center rounded-2xl bg-secondary text-primary">
              <Link2 className="h-6 w-6" />
            </span>
            <p className="font-display text-base font-semibold">{t("redirect.loading")}</p>
          </div>
        )}

        {state === "inactive" && (
          <Card className="card-soft text-center">
            <CardHeader>
              <p className="text-5xl">😶</p>
              <CardTitle className="text-xl">{t("redirect.inactive.title")}</CardTitle>
              <CardDescription>{t("redirect.inactive.text")}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild variant="secondary" className="w-full rounded-lg">
                <Link to="/">{t("nav.backHome")}</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {state === "password" && (
          <Card className="card-soft">
            <CardHeader className="text-center">
              <p className="text-5xl">🔒</p>
              <CardTitle className="text-xl">{t("redirect.password.title")}</CardTitle>
              <CardDescription>
                {title
                  ? t("redirect.password.named", { title })
                  : t("redirect.password.generic")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="link-password">{t("redirect.password.label")}</Label>
                  <Input
                    id="link-password"
                    type="password"
                    required
                    className="input-soft h-11"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  {wrongPassword && (
                    <p className="text-sm text-destructive">{t("redirect.password.wrong")}</p>
                  )}
                </div>
                <Button type="submit" className="w-full rounded-lg shadow-glow" disabled={submitting || !password}>
                  {t("redirect.password.submit")}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Redirect;
